<template>
  <page-header title="Maintenance">
    <button
      :disabled="isAdding"
      class="btn-primary ml-auto h-10"
      @click="addMaintenance"
    >
      <PlusCircleIcon class="size-5 -ml-1 mr-2" />
      Add
    </button>
  </page-header>

  <maintenance-form v-if="isAdding" @submit="onSubmit" @cancel="isAdding = false" />

  <grid-items-empty v-else-if="isEmpty" />

  <template v-else>
    <div class="flex justify-between mb-4">
      <reload-button class="ml-auto" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getRecods"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
      @finish="onFinish"
    />
  </template>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import GridItemsEmpty from '../components/grid/GridItemsEmpty.vue';
import Grid from '../components/grid/Grid.vue';
import MaintenanceActions from '../components/MaintenanceActions.vue';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import MaintenanceForm from '../components/MaintenanceForm.vue';
import { useToast } from 'vue-toastification';
import { Modal } from '../composables/useModal';
import { formatDate } from '../utils/formatters';

const toast = useToast();
const gridRef = ref(null);
const isEmpty = ref(false);
const isAdding = ref(false);
const isPendingMaintenance = ref(true);

function addMaintenance() {
  if (isAdding.value) {
    isAdding.value = false;
    return;
  }

  if (isPendingMaintenance.value) {
    toast.warning('There is already a pending or started maintenance task. Please complete or cancel it before adding a new one.');
    return;
  }
  isAdding.value = true;
}

async function onSubmit({ startAt }) {
  const { error } = await useFetch(`${apiDomain}/api/maintenance/`).post({ startAt });

  if (error.value) {
    toast.error(error.value);
    return;
  }

  isAdding.value = false;
  reloadGrid();
}

async function onFinish(row) {
  const confirm = await Modal.confirm({
    title: row.state === 'pending' ? 'Cancel Maintenance' : 'Complete Maintenance',
    body: 'Are you sure?',
    okButton: 'Yes',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { error } = await useFetch(`${apiDomain}/api/maintenance/${row.id}/finish`).get();

  if (error.value) {
    toast.error(error.value);
    return;
  }

  reloadGrid();
}

function rowClass(_row, index) { // FIXME: implement proper row class logic
  if (_row.state === 'pending') {
    return 'bg-primary-100/50';
  }
  if (_row.state === 'started') {
    return 'bg-fuchsia-100/50';
  }

  return index % 2 ? 'bg-zinc-200/10' : '';
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase() };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/maintenance/?${searchParams}`;
}

async function getRecods(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = !data.value.total;
  isPendingMaintenance.value = data.value.pendingCount > 0;

  return {
    total: data.value.total,
    rows: data.value.records
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    class: 'text-xs font-mono'
  },
  {
    title: 'Start At',
    prop: 'startAt',
    value: row => formatDate(row.startAt),
    class: '!py-0'
  },
  {
    title: 'Created',
    prop: 'createdAt',
    value: row => formatDate(row.createdAt)
  },
  {
    title: 'Finished',
    prop: 'finishedAt',
    value: row => formatDate(row.finishedAt)
  },
  {
    title: 'Status',
    prop: 'state',
    class: '!py-0'
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(MaintenanceActions)
  }
];
</script>
